import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { GitBranch, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ConditionNodeStatus = 'idle' | 'configured' | 'error'

export interface ConditionCanvasNodeData {
  label: string
  condition: string
  status?: ConditionNodeStatus
  errorMessage?: string
  variable?: string
  operator?: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty'
  value?: string
  trueBranchLabel?: string
  falseBranchLabel?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type ConditionCanvasNode = Node<ConditionCanvasNodeData, 'condition'>

const operatorLabels: Record<string, string> = {
  equals: '=',
  not_equals: '≠',
  contains: 'contains',
  greater_than: '>',
  less_than: '<',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
}

interface ConditionCanvasNodeProps {
  data: ConditionCanvasNodeData
  selected?: boolean
}

function ConditionCanvasNodeComponent({ data, selected }: ConditionCanvasNodeProps) {
  const getConditionSummary = () => {
    if (data.condition) return data.condition
    if (data.variable && data.operator) {
      const op = operatorLabels[data.operator] || data.operator
      if (data.operator === 'is_empty' || data.operator === 'is_not_empty') {
        return `${data.variable} ${op}`
      }
      return `${data.variable} ${op} ${data.value || '?'}`
    }
    return 'Set condition'
  }

  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[200px] max-w-[240px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-amber-500 ring-offset-2 dark:ring-offset-gray-900 border-amber-500'
      )}
    >
      {/* Left color accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/40">
              <GitBranch className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label || 'Condition'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono truncate">
              {getConditionSummary()}
            </p>
          </div>
        </div>

        {/* Branch labels */}
        <div className="mt-2 flex items-center justify-center gap-6">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            {data.trueBranchLabel || 'Yes'}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {data.falseBranchLabel || 'No'}
          </span>
        </div>

        {/* Error message */}
        {data.status === 'error' && data.errorMessage && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
              {data.errorMessage}
            </p>
          </div>
        )}
      </div>

      {/* Output Handles - Two outputs for branching */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5 !left-[30%]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        className="!w-2.5 !h-2.5 !bg-red-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5 !left-[70%]"
      />
    </div>
  )
}

export default memo(ConditionCanvasNodeComponent)
